import React, { useEffect, useState } from 'react';
import {
  Box, Button, ButtonText, Center, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import {
  AlertCircle, Camera, Clock, Monitor, ShieldCheck, Video,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { estimateBrowserCssZoom } from './estimateBrowserCssZoom';

export type LobbyWelcomeStepProps = {
  onComplete: () => void;
  selfService?: boolean;
};

const PREVIEW_ROWS = [
  { key: 'consent', icon: ShieldCheck },
  { key: 'conduct', icon: ShieldCheck },
  { key: 'screenShare', icon: Monitor },
  { key: 'device', icon: Video },
  { key: 'identity', icon: Camera },
  { key: 'waiting', icon: Clock },
] as const;

/**
 * Pre-flight step ⓪ — welcome overview before the numbered setup rail (R9).
 * Not part of LOBBY_STEPS; advances to Consent on "Begin set-up".
 */
export function LobbyWelcomeStep({ onComplete, selfService = false }: LobbyWelcomeStepProps) {
  const { t } = useTranslation();
  const [showZoomBanner, setShowZoomBanner] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setShowZoomBanner(estimateBrowserCssZoom(window.outerWidth, window.innerWidth));
  }, []);

  return (
    <Center flex={1} p="$6" testID="oral-exam-lobby-welcome-step">
      <VStack space="lg" maxWidth={560} width="100%" alignItems="center">
        <VStack space="sm" alignItems="center" width="100%">
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t('oralExam.lobby.welcome.title')}
          </Text>
          <Text size="md" color="$textBody" textAlign="center">
            {t('oralExam.lobby.welcome.subtitle')}
          </Text>
          <Text size="md" color="$textBody" textAlign="center">
            {t('oralExam.lobby.welcome.idReady')}
          </Text>
        </VStack>

        {showZoomBanner ? (
          <HStack
            space="sm"
            width="100%"
            bg="$statusErrorSubtle"
            borderRadius="$md"
            borderWidth={1}
            borderColor="$statusError"
            borderLeftWidth={4}
            borderLeftColor="$statusError"
            p="$4"
            alignItems="flex-start"
            testID="oral-exam-lobby-welcome-zoom-banner"
          >
            <Icon as={AlertCircle} size="sm" color="$statusError" mt="$0.5" />
            <Text size="sm" color="$textOnErrorSubtle" flex={1}>
              {t('oralExam.lobby.welcome.zoomBanner')}
            </Text>
          </HStack>
        ) : null}

        <VStack
          space="sm"
          width="100%"
          bg="$bgSubtle"
          borderRadius="$lg"
          borderWidth={1}
          borderColor="$borderLight"
          p="$4"
          testID="oral-exam-lobby-welcome-preview"
        >
          {PREVIEW_ROWS.map(({ key, icon: RowIcon }) => (
            <HStack
              key={key}
              space="sm"
              alignItems="center"
              testID={`oral-exam-lobby-welcome-preview-${key}`}
            >
              <Box
                w={32}
                h={32}
                borderRadius="$full"
                bg="$bgBrandSubtle"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={RowIcon} size="sm" color="$brandBase" />
              </Box>
              <Text size="md" color="$textBody" flex={1}>
                {key === 'waiting' && selfService
                  ? t('oralExam.lobby.welcome.preview.waitingSelfService', {
                    defaultValue: 'Start when you are ready',
                  })
                  : t(`oralExam.lobby.welcome.preview.${key}`)}
              </Text>
            </HStack>
          ))}
        </VStack>

        <Button
          size="md"
          onPress={onComplete}
          testID="oral-exam-lobby-welcome-begin"
          width="100%"
          maxWidth={280}
        >
          <ButtonText>{t('oralExam.lobby.welcome.beginSetup')}</ButtonText>
        </Button>
      </VStack>
    </Center>
  );
}
